<script setup lang="ts">
import { currencyMetadata, formatMoney, MINUS_SIGN, type Money, PLUS_SIGN } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    value: Money
    /** A flow is signed (`+`/`−`); a level (a balance, net worth) is not. */
    sign?: boolean
    /** Drops the currency symbol - for a column whose header already states the currency. */
    hideCurrency?: boolean
    /** Sets the symbol small and muted beside a bold figure (`245,487Ft`), the dashboard hero's style. */
    muteSymbol?: boolean
    /** Colours the figure; never a sign. */
    tone?: 'income' | 'expense' | 'transfer' | 'positive' | 'negative' | undefined
    size?: 'row' | 'figure' | 'medium' | 'large' | 'hero' | 'display'
    /** A dense matrix's body figures sit at regular weight; its total column keeps the default. */
    weight?: 'regular' | 'semibold'
  }>(),
  {
    sign: false,
    hideCurrency: false,
    muteSymbol: false,
    tone: undefined,
    size: 'figure',
    weight: 'semibold',
  },
)

const { locale } = useI18n()

const text = computed(() =>
  formatMoney(props.value, {
    locale: locale.value as 'en' | 'hu',
    sign: props.sign,
    showSymbol: !props.hideCurrency,
  }),
)

const figure = computed(() => {
  const body = formatMoney(props.value, {
    locale: locale.value as 'en' | 'hu',
    sign: props.sign,
    showSymbol: false,
  })
  const glyph = body.startsWith(MINUS_SIGN) || body.startsWith(PLUS_SIGN) ? body.slice(0, 1) : ''
  const metadata = currencyMetadata(props.value.currency)
  return {
    glyph,
    digits: body.slice(glyph.length),
    symbol: metadata.symbol,
    before: metadata.symbolPosition === 'before',
  }
})

const SYMBOL_CLASS = 'text-[length:var(--text-step-1)] font-normal text-[color:var(--color-text-muted)]'

const WEIGHT_CLASSES = { regular: 'font-normal', semibold: 'font-semibold' } as const
const SIZE_CLASSES = {
  row: 'text-[length:var(--text-step-2)]',
  figure: 'text-[length:var(--text-step-3)]',
  medium: 'text-[length:var(--text-step-4)]',
  large: 'text-[length:var(--text-step-5)]',
  hero: 'text-[length:var(--text-step-6)]',
  display: 'text-[length:var(--text-step-8)]',
} as const

const TONE_CLASSES = {
  income: 'text-[color:var(--color-income)]',
  expense: 'text-[color:var(--color-expense)]',
  transfer: 'text-[color:var(--color-transfer)]',
  positive: 'text-[color:var(--color-positive)]',
  negative: 'text-[color:var(--color-negative)]',
} as const
</script>

<template>
  <span
    class="inline-block whitespace-nowrap tabular-nums"
    :class="[
      SIZE_CLASSES[size],
      WEIGHT_CLASSES[weight],
      props.tone === undefined ? undefined : TONE_CLASSES[props.tone],
    ]"
    :style="{ fontFamily: 'var(--font-numeric)' }"
    ><template v-if="muteSymbol && !hideCurrency"
      >{{ figure.glyph }}<span v-if="figure.before" :class="SYMBOL_CLASS">{{ figure.symbol }}</span
      >{{ figure.digits
      }}<span v-if="!figure.before" :class="SYMBOL_CLASS">{{ figure.symbol }}</span></template
    ><template v-else>{{ text }}</template></span
  >
</template>
